import { LegacyIntegrationProvider } from "src/api/connectApi/types";

/**
 * Full API endpoints per resource for legacy integration providers.
 * Maps provider -> resource key -> { production, sandbox? } URLs.
 * Used for "View API Endpoints" display; no runtime gluing of base + path.
 */
export const LEGACY_PROVIDER_ENDPOINTS: Partial<
  Record<
    LegacyIntegrationProvider,
    Record<string, { production: string; sandbox?: string }>
  >
> = {
  plaid: {
    asset_report: {
      production: "https://production.plaid.com/credit/relay/get",
      sandbox: "https://sandbox.plaid.com/credit/relay/get",
    },
    financial_insights_report: {
      production: "https://production.plaid.com/credit/relay/get",
      sandbox: "https://sandbox.plaid.com/credit/relay/get",
    },
    plaid_cra_base_report: {
      production:
        "https://production.plaid.com/cra/check_report/base_report/get",
      sandbox: "https://sandbox.plaid.com/cra/check_report/base_report/get",
    },
    plaid_cra_income_insights_report: {
      production:
        "https://production.plaid.com/cra/check_report/income_insights/get",
      sandbox: "https://sandbox.plaid.com/cra/check_report/income_insights/get",
    },
    plaid_cra_partner_insights_report: {
      production:
        "https://production.plaid.com/cra/check_report/partner_insights/get",
      sandbox:
        "https://sandbox.plaid.com/cra/check_report/partner_insights/get",
    },
  },
  sardine: {
    sardine_identity_verification: {
      production: "https://api.sardine.ai/v1/customers",
      sandbox: "https://api.sandbox.sardine.ai/v1/customers",
    },
    sardine_feedback: {
      production: "https://api.sardine.ai/v1/feedbacks",
      sandbox: "https://api.sandbox.sardine.ai/v1/feedbacks",
    },
  },
  sentilink: {
    sentilink_synthetic_fraud_score: {
      production: "https://api.sentilink.com/v2/application",
      sandbox: "https://api.sandbox-sentilink.com/v2/application",
    },
    sentilink_identity_theft_score: {
      production: "https://api.sentilink.com/v2/application",
      sandbox: "https://api.sandbox-sentilink.com/v2/application",
    },
    sentilink_custom_score_products: {
      production: "https://api.sentilink.com/v2/application",
      sandbox: "https://api.sandbox-sentilink.com/v2/application",
    },
    sentilink_facets: {
      production: "https://api.sentilink.com/v2/application",
      sandbox: "https://api.sandbox-sentilink.com/v2/application",
    },
    sentilink_insights_report: {
      production: "https://api.sentilink.com/v1/insights/core",
      sandbox: "https://api.sandbox-sentilink.com/v1/insights/core",
    },
    sentilink_id_complete: {
      production: "https://api.sentilink.com/v1/query/user/complete",
      sandbox: "https://api.sandbox-sentilink.com/v1/query/user/complete",
    },
  },
  thomson_reuters: {
    thomson_reuters_clear_person_search: {
      production:
        "https://s2s.thomsonreuters.com/api/v2/eidvperson/searchResults/",
      sandbox:
        "https://s2s.beta.thomsonreuters.com/api/v2/eidvperson/searchResults/",
    },
    thomson_reuters_clear_business_search: {
      production:
        "https://s2s.thomsonreuters.com/api/v2/eidvbusiness/searchResults/",
      sandbox:
        "https://s2s.beta.thomsonreuters.com/api/v2/eidvbusiness/searchResults/",
    },
    thomson_reuters_risk_person_search: {
      production:
        "https://s2s.thomsonreuters.com/api/v3/riskinformperson/searchResults/",
      sandbox:
        "https://s2s.beta.thomsonreuters.com/api/v3/riskinformperson/searchResults/",
    },
    thomson_reuters_risk_business_search: {
      production:
        "https://s2s.thomsonreuters.com/api/v2/riskinformbusiness/searchResults/",
      sandbox:
        "https://s2s.beta.thomsonreuters.com/api/v2/riskinformbusiness/searchResults/",
    },
    thomson_reuters_person_report: {
      production:
        "https://s2s.thomsonreuters.com/api/v2/personReport/reportResults/",
      sandbox:
        "https://s2s.beta.thomsonreuters.com/api/v2/personReport/reportResults/",
    },
    thomson_reuters_business_report: {
      production:
        "https://s2s.thomsonreuters.com/api/v2/businessReport/reportResults/",
      sandbox:
        "https://s2s.beta.thomsonreuters.com/api/v2/businessReport/reportResults/",
    },
  },
  crif_b2c: {
    identity_check: {
      production: "https://api.crif.de/one-interface/0.5/snapshots/create",
      sandbox: "https://api.demo.crif.de/one-interface/0.5/snapshots/create",
    },
    credit_check: {
      production: "https://api.crif.de/one-interface/0.5/snapshots/create",
      sandbox: "https://api.demo.crif.de/one-interface/0.5/snapshots/create",
    },
  },
  data_x: {
    data_x_credit_report: {
      production: "https://verihub.com/datax/",
      sandbox: "https://rc.verihub.com/datax/",
    },
  },
  codat: {
    company_info: {
      production: "https://api.codat.io/companies/{company_id}/data/info",
      sandbox: "https://api.codat.io/companies/{company_id}/data/info",
    },
    company_identification: {
      production: "https://api.codat.io/companies",
      sandbox: "https://api.codat.io/companies",
    },
    enhanced_profit_and_loss_accounts: {
      production:
        "https://api.codat.io/companies/{company_id}/reports/enhancedProfitAndLoss/accounts",
      sandbox:
        "https://api.codat.io/companies/{company_id}/reports/enhancedProfitAndLoss/accounts",
    },
    enhanced_balance_sheet_accounts: {
      production:
        "https://api.codat.io/companies/{company_id}/reports/enhancedBalanceSheet/accounts",
      sandbox:
        "https://api.codat.io/companies/{company_id}/reports/enhancedBalanceSheet/accounts",
    },
    aged_creditors_report: {
      production:
        "https://api.codat.io/companies/{company_id}/reports/agedCreditor",
      sandbox:
        "https://api.codat.io/companies/{company_id}/reports/agedCreditor",
    },
    aged_debtors_report: {
      production:
        "https://api.codat.io/companies/{company_id}/reports/agedDebtor",
      sandbox: "https://api.codat.io/companies/{company_id}/reports/agedDebtor",
    },
  },
  equifax_canada: {
    equifax_canada_consumer_credit_file: {
      production: "https://api.equifax.ca/inquiry/1.0/sts",
      sandbox: "https://api.uat.equifax.ca/inquiry/1.0/sts",
    },
  },
  equifax_us: {
    scores_and_attributes: {
      production:
        "https://api.equifax.com/business/scores-and-attributes/v2/credit-report",
      sandbox:
        "https://api.uat.equifax.com/business/scores-and-attributes/v2/credit-report",
    },
    mla_report: {
      production:
        "https://api.equifax.com/business/military-lending-act/v1/report-requests",
      sandbox:
        "https://api.uat.equifax.com/business/military-lending-act/v1/report-requests",
    },
    one_view_report: {
      production:
        "https://api.equifax.com/business/oneview/consumer-credit/v1/reports/credit-report",
      sandbox:
        "https://api.uat.equifax.com/business/oneview/consumer-credit/v1/reports/credit-report",
    },
  },
  equifax_us_commercial_risk: {
    equifax_us_commercial_risk_credit_report: {
      production:
        "https://commercial-ppe.equifax.com/sbeppe/sbeservices/DecisionService",
      sandbox:
        "https://commercial-ppe-uat.equifax.com/sbeppe/sbeservices/DecisionService",
    },
  },
  trans_union: {
    tru_vision_model_report: {
      production: "https://netaccess.transunion.com",
      sandbox: "https://netaccess-test.transunion.com",
    },
    tru_vision_credit_report: {
      production: "https://netaccess.transunion.com",
      sandbox: "https://netaccess-test.transunion.com",
    },
  },
} as const;
